'use client';

import Image from '@/components/ui/CspImage';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ArrowRight, Clock } from 'lucide-react';

interface BlogCardProps {
  title: string;
  titleZh?: string;
  slug: string;
  excerpt?: string;
  excerptZh?: string;
  featuredImageUrl?: string;
  featuredImageAlt?: string;
  categoryName: string;
  categorySlug: string;
  categoryColor?: string;
  readingTimeMinutes?: number;
  publishedAt?: number;
  locale?: 'en' | 'zh'; // kept for backward compat but not used if next-intl available
}

// Category color mapping
const categoryColors: Record<string, { bg: string; text: string }> = {
  'fashion-trends': { bg: 'bg-[#942847]/10', text: 'text-[#942847]' },
  'product-updates': { bg: 'bg-blue-500/10', text: 'text-blue-600' },
  'tutorials': { bg: 'bg-emerald-500/10', text: 'text-emerald-600' },
  'company-news': { bg: 'bg-violet-500/10', text: 'text-violet-600' },
  'ai-insights': { bg: 'bg-red-500/10', text: 'text-red-600' },
};

export default function BlogCard({
  title,
  titleZh,
  slug,
  excerpt,
  excerptZh,
  featuredImageUrl,
  featuredImageAlt,
  categoryName,
  categorySlug,
  readingTimeMinutes,
}: BlogCardProps) {
  const intlLocale = useLocale();
  const t = useTranslations('blog');
  const isZh = intlLocale === 'zh-TW';
  const displayTitle = isZh && titleZh ? titleZh : title;
  const displayExcerpt = isZh && excerptZh ? excerptZh : excerpt;
  const colors = categoryColors[categorySlug] || categoryColors['fashion-trends'];

  return (
    <Link href={`/blog/${slug}`} className="group block">
      <article className="relative bg-card rounded-2xl border border-border hover:border-primary/30 overflow-hidden transition-all duration-300 hover:shadow-elevated-lg hover:-translate-y-1.5 card-accent">
        {/* Featured Image */}
        <div className="relative aspect-[16/10] overflow-hidden">
          {featuredImageUrl ? (
            <Image
              src={featuredImageUrl}
              alt={featuredImageAlt || displayTitle}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <div className="text-primary/40 text-6xl font-display">{displayTitle.charAt(0)}</div>
            </div>
          )}
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Category Badge */}
          <div className="flex items-center gap-3 mb-3">
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}>
              {categoryName}
            </span>
            {readingTimeMinutes && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {t('minRead', { minutes: readingTimeMinutes })}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-display text-xl font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {displayTitle}
          </h3>

          {/* Excerpt */}
          {displayExcerpt && (
            <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2 mb-4">
              {displayExcerpt}
            </p>
          )}

          {/* Read More */}
          <div className="flex items-center text-primary font-medium text-sm group/link">
            <span>{t('readMore')}</span>
            <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </article>
    </Link>
  );
}
